import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApi, useLoad, errMsg } from "@/lib/api";
import { card, Field, Sel, inputCls, HospitalSelect, Empty } from "@/components/admin/kit";

const A0 = (pid) => ({ patient_id: pid, type: "followup", hospital_id: "", doctor_id: "", date: "", time_mode: "morning", time: "", instruction: "", treatment: "", sitting: 1 });
const SIT = [1, 2, 3, 4, 5, 6].map((n) => [String(n), ["1st", "2nd", "3rd", "4th", "5th", "6th"][n - 1]]);

function PresetText({ label, value, onChange, presets, testid }) {
  return (
    <Field label={label} hint="Pick from the list or type your own (Telugu)">
      <Sel value={presets.some((p) => p[1] === value) ? value : ""} onChange={onChange} placeholder="— type below —" options={presets.map((p) => [p[1], p[0]])} testid={`${testid}-preset`} />
      <input className={`${inputCls} font-te mt-1`} value={value} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-input`} />
    </Field>
  );
}

function AppointmentForm({ pid, opts, onSaved }) {
  const api = useApi();
  const [f, setF] = useState(A0(pid));
  const [docs] = useLoad(f.hospital_id ? `/doctors?hospital_id=${f.hospital_id}` : null, [f.hospital_id]);
  const s = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });
  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/appointments", { ...f, doctor_id: f.doctor_id || null, sitting: Number(f.sitting) }); setF(A0(pid)); onSaved(); toast.success("Saved — the reminder call is in the call plan"); } catch (err) { toast.error(errMsg(err)); }
  };
  const dental = f.type === "dental";
  return (
    <form onSubmit={save} className="mt-3 grid gap-3 sm:grid-cols-3" data-testid="appointment-form">
      <Field label="Type"><Sel value={f.type} onChange={s("type")} options={[["followup", "Follow-up / check-up (call 2 days before)"], ["dental", "Dental / procedure sitting (call 1 day before)"]]} testid="appt-type-select" /></Field>
      <Field label="Hospital / clinic *"><HospitalSelect value={f.hospital_id} onChange={(v) => setF({ ...f, hospital_id: v, doctor_id: "" })} testid="appt-hospital-select" /></Field>
      <Field label="Doctor"><Sel value={f.doctor_id} onChange={s("doctor_id")} placeholder="—" options={(docs || []).map((d) => [d.id, d.name])} testid="appt-doctor-select" /></Field>
      <Field label="Date *"><input type="date" required className={inputCls} value={f.date} onChange={s("date")} data-testid="appt-date-input" /></Field>
      <Field label="Time"><Sel value={f.time_mode} onChange={s("time_mode")} options={[["morning", "Morning"], ["evening", "Evening"], ["exact", "Exact time"]]} testid="appt-time-mode-select" /></Field>
      {f.time_mode === "exact" ? <Field label="Exact time *"><input type="time" className={inputCls} value={f.time} onChange={s("time")} data-testid="appt-time-input" /></Field> : <div />}
      {dental && <PresetText label="Treatment *" value={f.treatment} onChange={s("treatment")} presets={opts?.treatments || []} testid="appt-treatment" />}
      {dental && <Field label="Sitting number"><Sel value={String(f.sitting)} onChange={s("sitting")} options={SIT} testid="appt-sitting-select" /></Field>}
      {!dental && <PresetText label="Instruction (optional)" value={f.instruction} onChange={s("instruction")} presets={opts?.instructions || []} testid="appt-instruction" />}
      <div className="flex items-end"><Button disabled={!f.hospital_id || !f.date} data-testid="appt-save-btn"><Plus size={14} /> Add appointment</Button></div>
    </form>
  );
}

export function Appointments({ pid }) {
  const api = useApi();
  const [list, reload] = useLoad(`/appointments?patient_id=${pid}`);
  const [opts] = useLoad("/call-types/options");
  return (
    <div className={`${card} mt-4`} data-testid="appointments-panel">
      <p className="font-bold">Check-ups & sittings</p>
      <p className="mb-2 text-xs text-muted-foreground">One reminder call at 10 AM: 2 days before a check-up, 1 day before a dental / procedure sitting.</p>
      {!(list || []).length ? <Empty>No appointments yet.</Empty> : list.map((a) => (
        <div key={a.id} className="flex flex-wrap items-center gap-2 border-b border-[var(--lav-2)] py-2 text-sm" data-testid={`appointment-${a.id}`}>
          <span className="rounded-full bg-[var(--lav-2)] px-2 text-xs font-semibold">{a.type === "dental" ? "Sitting" : "Check-up"}</span>
          <b>{a.date}</b><span className="text-muted-foreground">{a.time_mode === "exact" ? a.time : a.time_mode}</span>
          <span className="font-te">{a.treatment} {a.type === "dental" ? `· sitting ${a.sitting}` : a.instruction}</span>
          <button className="ml-auto text-muted-foreground" onClick={async () => { await api.delete(`/appointments/${a.id}`); reload(); }} data-testid={`appointment-delete-${a.id}`}><Trash2 size={14} /></button>
        </div>
      ))}
      <AppointmentForm pid={pid} opts={opts} onSaved={reload} />
    </div>
  );
}
